/**
 * Cooking recipe definitions — data-driven dish configuration.
 *
 * Round 13 Phase C. Source of truth: loot-and-materials.md section 7.
 *
 * Each recipe maps ingredient ItemTypes to a resulting food ItemType,
 * with a buff type, duration, and feeling-based description.
 *
 * @packageDocumentation
 */
import { ItemType } from '../types/loot.js';
/** 1 hour in ms */
const HOUR = 60 * 60 * 1000;
export const COOKING_RECIPES = [
    // ── Bread: small stamina regen, 1h ──
    {
        id: 'recipe_bread',
        name: 'Hearth Bread',
        description: 'Simple flatbread baked on hearthstones.',
        ingredients: [
            { itemType: ItemType.MaterialThatch, quantity: 1 },
            { itemType: ItemType.MaterialThawedWater, quantity: 1 },
        ],
        resultItemType: ItemType.FoodBread,
        cookTimeSeconds: 15,
        buffType: 'stamina_regen',
        buffDurationMs: 1 * HOUR,
        feeling: 'The warm bread settles in your stomach. You feel steadier on your feet.',
    },
    // ── Stew variants: ghost-decay resist, 4h ──
    {
        id: 'recipe_stew_verdana',
        name: 'Verdana Thicket Stew',
        description: 'A hearty stew with forest herbs.',
        ingredients: [
            { itemType: ItemType.MaterialWood, quantity: 2 },
            { itemType: ItemType.MaterialThatch, quantity: 1 },
            { itemType: ItemType.MaterialThawedWater, quantity: 1 },
        ],
        resultItemType: ItemType.FoodStewVerdana,
        cookTimeSeconds: 30,
        buffType: 'ghost_decay_resist',
        buffDurationMs: 4 * HOUR,
        feeling: 'The stew warms you from the inside. The cold feels further away now.',
    },
    {
        id: 'recipe_stew_sablewind',
        name: 'Sablewind Ash Broth',
        description: 'Dark broth thickened with volcanic ash.',
        ingredients: [
            { itemType: ItemType.MaterialWood, quantity: 2 },
            { itemType: ItemType.MaterialAsh, quantity: 1 },
            { itemType: ItemType.MaterialThawedWater, quantity: 1 },
        ],
        resultItemType: ItemType.FoodStewSablewind,
        cookTimeSeconds: 30,
        buffType: 'ghost_decay_resist',
        buffDurationMs: 4 * HOUR,
        feeling: 'Something in the broth anchors you. The ghosts seem less interested.',
    },
    {
        id: 'recipe_stew_frostmere',
        name: 'Frostmere Cloth-Wrap Stew',
        description: 'Ingredients bundled in cloth and slow-simmered.',
        ingredients: [
            { itemType: ItemType.MaterialWood, quantity: 2 },
            { itemType: ItemType.MaterialCloth, quantity: 1 },
            { itemType: ItemType.MaterialThawedWater, quantity: 1 },
        ],
        resultItemType: ItemType.FoodStewFrostmere,
        cookTimeSeconds: 30,
        buffType: 'ghost_decay_resist',
        buffDurationMs: 4 * HOUR,
        feeling: 'The stew fills you with a steady warmth. You feel less likely to fade.',
    },
    {
        id: 'recipe_stew_embermire',
        name: 'Ember Mire Char Stew',
        description: 'A smoky stew with ember-singed roots.',
        ingredients: [
            { itemType: ItemType.MaterialWood, quantity: 2 },
            { itemType: ItemType.MaterialEmber, quantity: 1 },
            { itemType: ItemType.MaterialThawedWater, quantity: 1 },
        ],
        resultItemType: ItemType.FoodStewEmberMire,
        cookTimeSeconds: 30,
        buffType: 'ghost_decay_resist',
        buffDurationMs: 4 * HOUR,
        feeling: 'Embers still glow in the broth. You feel stubborn against the dark.',
    },
    // ── Tea: weather awareness, 2h ──
    {
        id: 'recipe_tea',
        name: 'Wax-Sealed Tea',
        description: 'Fragrant leaves sealed with beeswax.',
        ingredients: [
            { itemType: ItemType.MaterialWax, quantity: 1 },
            { itemType: ItemType.MaterialThawedWater, quantity: 1 },
        ],
        resultItemType: ItemType.FoodTea,
        cookTimeSeconds: 20,
        buffType: 'weather_awareness',
        buffDurationMs: 2 * HOUR,
        feeling: 'The tea sharpens your senses. You can feel the weather shifting before it arrives.',
    },
    // ── Feast: group buff, 24h ──
    {
        id: 'recipe_feast',
        name: 'Communal Feast',
        description: 'A grand meal that requires many hands and ingredients.',
        ingredients: [
            { itemType: ItemType.MaterialWood, quantity: 2 },
            { itemType: ItemType.MaterialThatch, quantity: 1 },
            { itemType: ItemType.MaterialWax, quantity: 1 },
            { itemType: ItemType.MaterialThawedWater, quantity: 2 },
        ],
        resultItemType: ItemType.FoodFeast,
        cookTimeSeconds: 60,
        buffType: 'group_buff',
        buffDurationMs: 24 * HOUR,
        feeling: 'The feast fills everyone with a deep sense of belonging. The world feels safer together.',
        requiresGroupCook: true,
        minCooksPresent: 2,
    },
    // ── Pet Dish: bonding raise ──
    {
        id: 'recipe_pet_dish',
        name: 'Pet Treat',
        description: 'A morsel prepared with care for a companion.',
        ingredients: [
            { itemType: ItemType.MaterialThatch, quantity: 1 },
        ],
        resultItemType: ItemType.FoodPetDish,
        cookTimeSeconds: 10,
        buffType: 'pet_bonding',
        buffDurationMs: 0, // Instant effect — bonding raise, no timed buff
        feeling: 'Your companion sniffs the treat eagerly.',
    },
];
/** Look up a cooking recipe by id. */
export function getCookingRecipe(recipeId) {
    return COOKING_RECIPES.find(r => r.id === recipeId);
}
//# sourceMappingURL=cookingRecipes.js.map